/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import './style/connectionComplete.scss';

import React, {MouseEventHandler, ReactElement} from 'react';

import {useTranslation} from 'react-i18next';

import fileTextFilled from '@iconify/icons-ant-design/file-text-filled';

import Bell from '@iconify/icons-mdi/bell-outline';

import {formatInvoiceNumber, replaceDateVariables} from '../../util/utils';

import {AwesomeIcon, ChangeFunction, ChangeKeyFunction, Checkbox, Loader, voidFunction} from '@dvrd/dvr-controls';

import BolmateRadio from '../controls/radio/bolmateRadio';

import BolmateButton from '../controls/button/bolmateButton';

import {Icon} from '@iconify/react';

import classNames from 'classnames';

import {BolmateInput, BolmateNumberInput} from '../controls';

import Account from '../../models/accountModel';

import {AutoUploadType, InvoiceSettingsErrors, ResetErrorHandler} from '../../util/interfaces';

import {ConnectionSettingsStep, InvoiceNumberType} from './connectionCompleteController';

import Image from '../misc/image';

import {NotificationType} from '../../util/enums';

import {NotificationTypeParam} from '../../util/types';

import {BolmateBadge} from '../controls';

interface Props {
    onSelectInvoiceNumberType: (numType: InvoiceNumberType) => MouseEventHandler;
    onClickNext: MouseEventHandler;
    onClickBack: MouseEventHandler;
    onChangeInvoiceNum: (nextNum: string) => void;
    onChangeSetting: ChangeKeyFunction;
    onChangeImportHistory: (import30Days: boolean) => MouseEventHandler;
    onResetError: ResetErrorHandler<InvoiceSettingsErrors>;
    onChooseAutoUpload: (value: AutoUploadType, skip?: boolean) => MouseEventHandler;
    onChangeAutoUploadOld: (value: boolean | null) => MouseEventHandler;
    onChangeOldInvoiceNumStart: ChangeFunction<string>;
    onChangeOldInvoiceNumPrefix: ChangeFunction<string>;
    onToggleNotification: (notificationType: NotificationType) => MouseEventHandler;
    importHistory: boolean | null;
    invoiceNumberType: InvoiceNumberType | null;
    account: Account;
    errors: InvoiceSettingsErrors;
    invoicePreview: string;
    invoicePrefix: string;
    invoiceStart: string;
    step: ConnectionSettingsStep;
    loading: boolean;
    autoUpload: AutoUploadType | null;
    autoUploadOld: boolean | null;
    oldInvoiceNumPrefix: string;
    oldInvoiceNumStart: string;
    enableNotifications: Set<NotificationType>;
}

export default function ConnectionComplete(props: Props) {
    const {
        onSelectInvoiceNumberType,
        onClickNext,
        onClickBack,
        onChangeInvoiceNum,
        onChangeSetting,
        onChangeImportHistory,
        onResetError,
        importHistory,
        invoiceNumberType,
        account,
        invoicePreview,
        invoicePrefix,
        step,
        loading,
        invoiceStart,
        errors,
        onChooseAutoUpload,
        autoUpload,
        oldInvoiceNumStart,
        oldInvoiceNumPrefix,
        autoUploadOld,
        onChangeAutoUploadOld,
        onChangeOldInvoiceNumPrefix,
        onChangeOldInvoiceNumStart,
        onToggleNotification,
        enableNotifications
    } = props;
    const {t} = useTranslation();

    function renderYesButton(onClick: MouseEventHandler, value: boolean) {
        return (
            <div className='body-container'>
                <div className='flow' onClick={onClick}>
                    <div className='texts-container'>
                        <label className='flow-title'>{t('connectionComplete.yes', 'Ja')}</label>
                    </div>
                    <BolmateRadio onSelect={voidFunction} selected={value} />
                </div>
            </div>
        );
    }

    function renderNoButton(onClick: MouseEventHandler, value: boolean) {
        return (
            <div className='body-container'>
                <div className='flow' onClick={onClick}>
                    <div className='texts-container'>
                        <label className='flow-title'>{t('connectionComplete.no', 'Nee')}</label>
                    </div>
                    <BolmateRadio onSelect={voidFunction} selected={value} />
                </div>
            </div>
        );
    }

    function renderBackButton() {
        return (
            <BolmateButton
                onClick={onClickBack}
                label={t('connectionComplete.back', 'Terug')}
                secondary
                ornaments={{element: <AwesomeIcon name='arrow-left' />}}
            />
        );
    }

    function renderInvoicePreview(invoicePrefix: string) {
        const count = Number(invoicePreview);
        const editable = !Number.isNaN(count);
        const onUp = () => {
            const nextCount = count + 1;
            const nextNum = formatInvoiceNumber(nextCount, invoiceStart);
            onChangeInvoiceNum(nextNum);
        };
        const onDown = () => {
            const nextCount = count - 1;
            const nextNum = formatInvoiceNumber(nextCount, invoiceStart);
            onChangeInvoiceNum(nextNum);
        };
        const prefix = replaceDateVariables(invoicePrefix);
        const min = Number(invoiceStart);
        return (
            <div className='invoice-number-preview'>
                <label className='preview'>
                    {t('connectionComplete.preview', 'Voorbeeld:')} {prefix}
                    {invoicePreview}
                </label>
                {editable && (
                    <div className='editors'>
                        <span className='editor' onClick={onUp}>
                            +
                        </span>
                        <span
                            className={classNames('editor', count <= min && 'disabled')}
                            onClick={count > min ? onDown : voidFunction}
                        >
                            -
                        </span>
                    </div>
                )}
            </div>
        );
    }

    function renderStandard() {
        const {settings} = account;
        const count = Number(settings.invoiceStart);
        const length = settings.invoiceStart.length;
        const nextCount = count + 49;
        const repeatMax = Math.max(length - nextCount.toString().length, 0);
        const nextNum = '0'.repeat(repeatMax) + nextCount;
        return (
            <div className='body-container'>
                <div className='flow' onClick={onSelectInvoiceNumberType(InvoiceNumberType.STANDARD)}>
                    <div className='texts-container'>
                        <label className='flow-title'>{t('connectionComplete.standard', 'Standaard')}</label>
                        <label className='text-example'>
                            {t('connectionComplete.configured', 'Ingesteld:')} {settings.invoicePrefix}
                            {settings.invoiceStart}
                        </label>
                    </div>
                    <BolmateRadio onSelect={voidFunction} selected={invoiceNumberType === InvoiceNumberType.STANDARD} />
                </div>
                <div className='info-container'>
                    <AwesomeIcon name='info-circle' className='info-icon' />
                    <label className='info-text'>
                        {t('connectionComplete.standardInfo.example', 'Voorbeeld:')}
                        <br />
                        {t(
                            'connectionComplete.standardInfo.line1',
                            'Je hebt 50 orders gehad in de afgelopen 30 dagen.'
                        )}
                        <br />
                        {t('connectionComplete.standardInfo.line2', 'Deze krijgen de nummering')}{' '}
                        {replaceDateVariables(settings.invoicePrefix)}
                        {settings.invoiceStart} {t('connectionComplete.standardInfo.until', 'tot')}{' '}
                        {replaceDateVariables(settings.invoicePrefix)}
                        {nextNum}
                        <br />
                        {t('connectionComplete.standardInfo.line3', 'op volgorde chronologisch in de tijd.')}
                        <br />
                        {t(
                            'connectionComplete.standardInfo.line4',
                            'De orders die je nieuw binnenkrijgt, tellen door vanaf dit punt.'
                        )}
                        <br />
                        {t('connectionComplete.standardInfo.line5', 'Die krijgen dus de nummering')}{' '}
                        {replaceDateVariables(settings.invoicePrefix)}
                        {'0'.repeat(repeatMax) + (nextCount + 1)},{' '}
                        {replaceDateVariables(account.settings.invoicePrefix)}
                        {'0'.repeat(repeatMax) + (nextCount + 2)}, {t('connectionComplete.standardInfo.etc', 'etc.')}
                    </label>
                </div>
            </div>
        );
    }

    function renderCustom() {
        const count = Number(invoiceStart);
        const countStandard = Number(account.settings.invoiceStart);
        const length = invoiceStart.length;
        const nextCountStandard = countStandard + 1;
        const repeatMaxStandard = Math.max(length - nextCountStandard.toString().length, 0);
        const nextCount = count + 49;
        const repeatMax = Math.max(length - nextCount.toString().length, 0);
        const selected = invoiceNumberType === InvoiceNumberType.CUSTOM;
        return (
            <div className='body-container'>
                <div className='flow' onClick={onSelectInvoiceNumberType(InvoiceNumberType.CUSTOM)}>
                    <div className='texts-container'>
                        <label className='flow-title'>{t('connectionComplete.custom', 'Anders')}</label>
                        {(invoiceNumberType === InvoiceNumberType.STANDARD || invoiceNumberType === null) && (
                            <label className='text-example'>
                                {t('connectionComplete.customNamely', 'Namelijk...')}
                            </label>
                        )}
                    </div>
                    <BolmateRadio onSelect={voidFunction} selected={invoiceNumberType === InvoiceNumberType.CUSTOM} />
                    {renderInvoiceNumberEditor(
                        invoiceStart,
                        invoicePrefix,
                        onChangeSetting('invoice_prefix'),
                        onChangeSetting('invoice_start'),
                        'invoicePrefix',
                        'invoiceStart',
                        selected
                    )}
                </div>
                <div className='info-container'>
                    <AwesomeIcon name='info-circle' className='info-icon' />
                    <label className='info-text'>
                        {t('connectionComplete.customInfo.example', 'Voorbeeld:')}
                        <br />
                        {t('connectionComplete.customInfo.line1', 'Je hebt 50 orders gehad in de afgelopen 30 dagen.')}
                        <br />
                        {t(
                            'connectionComplete.customInfo.line2',
                            'Je stelt hiervoor een andere factuurnummering in namelijk'
                        )}{' '}
                        {replaceDateVariables(invoicePrefix)}
                        {invoiceStart}.<br />
                        {t('connectionComplete.customInfo.line3', 'Dan krijgt de import de nummering')}{' '}
                        {replaceDateVariables(invoicePrefix)}
                        {invoiceStart} {t('connectionComplete.customInfo.until', 'tot')}{' '}
                        {replaceDateVariables(invoicePrefix)}
                        {'0'.repeat(repeatMax) + nextCount}
                        <br />
                        {t('connectionComplete.customInfo.line4', 'op volgorde chronologisch in de tijd.')}
                        <br />
                        {t(
                            'connectionComplete.customInfo.line5',
                            'De orders die je daarna nieuw binnenkrijgt, volgen de standaard factuurnummering.'
                        )}
                        <br />
                        {t('connectionComplete.customInfo.line6', 'Deze krijgen dus de nummering')}{' '}
                        {replaceDateVariables(account.settings.invoicePrefix)}
                        {account.settings.invoiceStart}, {replaceDateVariables(account.settings.invoicePrefix)}
                        {'0'.repeat(repeatMaxStandard) + nextCountStandard}{' '}
                        {t('connectionComplete.customInfo.etc', 'etc.')}
                    </label>
                </div>
            </div>
        );
    }

    function renderInvoiceNumberEditor(
        invoiceStart: string,
        invoicePrefix: string,
        onChangePrefix: ChangeFunction<string>,
        onChangeStart: ChangeFunction<string>,
        prefixErrorKey: keyof InvoiceSettingsErrors,
        startErrorKey: keyof InvoiceSettingsErrors,
        selected: boolean,
        className?: string
    ) {
        return (
            <div className={classNames('invoice-number', selected && 'selected', className)}>
                <div className='row'>
                    <BolmateInput
                        value={invoicePrefix}
                        onChange={onChangePrefix}
                        error={errors[prefixErrorKey]}
                        label={t('connectionComplete.prefix', 'Voortekens *')}
                        onFocus={onResetError(prefixErrorKey)}
                        autoFocus
                    />
                    <BolmateNumberInput
                        value={invoiceStart}
                        onChange={onChangeStart}
                        error={errors[startErrorKey]}
                        label={t('connectionComplete.start', 'Beginnen bij *')}
                        onFocus={onResetError(startErrorKey)}
                        wholeNumbers
                        disableSymbols
                    />
                </div>
                {renderInvoicePreview(invoicePrefix)}
            </div>
        );
    }

    function renderIntro() {
        return (
            <>
                <div className='icon-container background'>
                    <AwesomeIcon name='check' className='check-icon' />
                </div>
                <label className='text-title'>
                    {t('connectionComplete.intro.title', 'De koppeling met Bol is gemaakt!')}
                </label>
                <div className='text-subtitle-container'>
                    <label className='text-subtitle'>
                        {t('connectionComplete.intro.subtitle', 'Vanaf nu halen we alle nieuwe orders op bij Bol')}
                    </label>
                </div>
                <div className='button-container-single'>
                    <BolmateButton onClick={onClickNext} label={t('connectionComplete.next', 'Volgende')} />
                </div>
            </>
        );
    }

    function renderImportHistory() {
        return (
            <>
                <div className='icon-container'>
                    <Icon icon={fileTextFilled} className='invoice-icon' />
                </div>
                <label className='text-title'>
                    {t(
                        'connectionComplete.importHistory.title',
                        'Wil je ook de orders van de afgelopen 30 dagen importeren?'
                    )}
                </label>
                <div className='text-subtitle-container flex'>
                    <label className='text-subtitle'>
                        {t(
                            'connectionComplete.importHistory.subtitle',
                            'Dit kan even duren, afhankelijk van de hoeveelheid orders.'
                        )}
                        <br />
                    </label>
                    <div className='info-container'>
                        <AwesomeIcon name='info-circle' className='info-icon' />
                        <label className='info-text import-times'>
                            {t(
                                'connectionComplete.importHistory.info1',
                                'De facturen zijn pas beschikbaar als het importeren klaar is.'
                            )}
                            <br />
                            {t(
                                'connectionComplete.importHistory.info2',
                                'In de tussentijd kun je wel de rest van het platform gebruiken.'
                            )}
                            <br />
                            {t('connectionComplete.importHistory.info3', 'Indicatie van de tijd die het duurt:')}
                            <br />
                            <br />
                            {t('connectionComplete.importHistory.time1', '100 orders = 1 minuut')} <br />
                            {t('connectionComplete.importHistory.time2', '1000 orders = 1,5 minuten')} <br />
                            {t('connectionComplete.importHistory.time3', '5000 orders = 2 minuten')}
                            <br />
                            ...
                        </label>
                    </div>
                </div>
                {renderYesButton(onChangeImportHistory(true), !!importHistory)}
                {renderNoButton(onChangeImportHistory(false), importHistory === false)}
                <div className='button-container-double'>
                    {renderBackButton()}
                    <BolmateButton
                        onClick={onClickNext}
                        label={t('connectionComplete.next', 'Volgende')}
                        disabled={importHistory === null}
                    />
                </div>
            </>
        );
    }

    function renderInvoiceNumber() {
        return (
            <>
                <div className='icon-container'>
                    <Icon icon={fileTextFilled} className='invoice-icon' />
                </div>
                <label className='text-title'>
                    {t('connectionComplete.invoiceNumber.title', 'Welke factuur nummering wil je hiervoor gebruiken?')}
                </label>
                <div className='text-subtitle-container'>
                    <label className='text-subtitle'>
                        {t(
                            'connectionComplete.invoiceNumber.subtitle1',
                            'Wil je dezelfde of een andere factuurnummering gebruiken'
                        )}{' '}
                        <br />
                        {t(
                            'connectionComplete.invoiceNumber.subtitle2',
                            'voor de import van de orders van de afgelopen 30 dagen?'
                        )}
                    </label>
                </div>
                {renderStandard()}
                {renderCustom()}
                <div className='button-container-double'>
                    {renderBackButton()}
                    <BolmateButton
                        onClick={onClickNext}
                        label={t('connectionComplete.next', 'Volgende')}
                        disabled={invoiceNumberType === null}
                    />
                </div>
            </>
        );
    }

    function renderAutoUpload() {
        return (
            <>
                <div className='icon-container'>
                    <Icon icon={fileTextFilled} className='invoice-icon' />
                </div>
                <label className='text-title'>
                    {t('connectionComplete.autoUpload.title', 'Facturen automatisch laten uploaden naar Bol?')}
                </label>
                <div className='text-subtitle-container flex'>
                    <label className='text-subtitle'>
                        {t('connectionComplete.autoUpload.subtitle', 'Zo ben je factuuraanvragen voor.')}
                        <br />
                    </label>
                    <div className='info-container'>
                        <AwesomeIcon name='info-circle' className='info-icon' />
                        <label className='info-text left'>
                            {t(
                                'connectionComplete.autoUpload.info1',
                                'Sinds juni 2023 kunnen facturen direct naar het platform van Bol geüpload worden. Als een klant een aanvraag indient voor een factuur, dan ben je verplicht deze binnen 24 uur te uploaden naar Bol. Je kunt met Bolmate factuuraanvragen voor zijn door facturen proactief te laten uploaden. Daarnaast kan Bolmate automatisch jouw factuuraanvragen beantwoorden door de factuur te uploaden.'
                            )}
                            <br />
                            <br />
                            {t(
                                'connectionComplete.autoUpload.info2',
                                'Je kunt deze keuze altijd nog aanpassen en jouw specifieke upload wensen instellen onder het kopje Aanvragen in het menu.'
                            )}
                        </label>
                    </div>
                </div>
                {renderAutoUploadButton(AutoUploadType.BOTH)}
                {renderAutoUploadButton(AutoUploadType.NONE)}
                <label className='skip-label' onClick={onChooseAutoUpload(AutoUploadType.NONE, true)}>
                    {t('connectionComplete.autoUpload.decideLater', 'Later beslissen')}
                </label>
                <div className='button-container-double'>
                    {renderBackButton()}
                    <BolmateButton
                        onClick={onClickNext}
                        label={t('connectionComplete.next', 'Volgende')}
                        disabled={autoUpload === null}
                    />
                </div>
            </>
        );
    }

    function renderAutoUploadButton(value: AutoUploadType.BOTH | AutoUploadType.NONE) {
        const selected = value === autoUpload;
        let title: string;
        if (value === AutoUploadType.BOTH) title = t('connectionComplete.yes', 'Ja');
        else title = t('connectionComplete.no', 'Nee');
        return (
            <div className='body-container'>
                <div className='flow' onClick={onChooseAutoUpload(value)}>
                    <div className='texts-container'>
                        <label className='flow-title'>{title}</label>
                    </div>
                    <BolmateRadio onSelect={voidFunction} selected={selected} />
                </div>
            </div>
        );
    }

    function renderAutoUploadOld() {
        return (
            <>
                <div className='icon-container'>
                    <Icon icon={fileTextFilled} className='invoice-icon' />
                </div>
                <label className='text-title'>
                    {t(
                        'connectionComplete.autoUploadOld.title',
                        'Factuuraanvragen voor oudere orders automatisch verwerken?'
                    )}
                </label>
                <div className='text-subtitle-container flex'>
                    <label className='text-subtitle'>
                        {t(
                            'connectionComplete.autoUploadOld.subtitle',
                            'Deze worden verwerkt met een andere factuurnummer reeks.'
                        )}
                        <br />
                    </label>
                    <div className='info-container'>
                        <AwesomeIcon name='info-circle' className='info-icon' />
                        <label className='info-text left'>
                            <label className='bold-text'>
                                {t('connectionComplete.autoUploadOld.heading', 'Factuuraanvragen voor oude orders')}
                            </label>
                            <br />
                            <br />
                            {t(
                                'connectionComplete.autoUploadOld.info1',
                                'Jouw klanten kunnen voor orders tot 2 jaar terug een factuur aanvragen. Het is mogelijk dat wij deze order niet in het systeem hebben, omdat jouw Bolmate account toen nog niet bestond.'
                            )}
                            <br />
                            <br />
                            {t(
                                'connectionComplete.autoUploadOld.info2',
                                'We kunnen deze factuuraanvraag wel automatisch beantwoorden met een factuur upload, maar doordat de Belastingdienst vereist dat jouw factuurnummering chronologisch oploopt, kunnen we hiervoor niet het standaard factuurnummer gebruiken.'
                            )}
                            <br />
                            <br />
                            {t(
                                'connectionComplete.autoUploadOld.info3',
                                'Je kunt daarom een andere factuurnummerreeks kiezen voor de facturen voor oude orders, op deze manier blijven de nummers chronologisch oplopend.'
                            )}
                        </label>
                    </div>
                </div>
                {renderAutoUploadOldButton(true)}
                {renderAutoUploadOldButton(false)}
                <label className='skip-label' onClick={onChangeAutoUploadOld(null)}>
                    {t('connectionComplete.autoUpload.decideLater', 'Later beslissen')}
                </label>
                <div className='button-container-double'>
                    {renderBackButton()}
                    <BolmateButton
                        onClick={onClickNext}
                        label={t('connectionComplete.next', 'Volgende')}
                        disabled={autoUpload === null}
                    />
                </div>
            </>
        );
    }

    function renderAutoUploadOldButton(value: boolean) {
        const selected = value === autoUploadOld;
        const title = value ? t('connectionComplete.yes', 'Ja') : t('connectionComplete.no', 'Nee');
        return (
            <div className='body-container'>
                <div className='flow' onClick={onChangeAutoUploadOld(value)}>
                    <div className='texts-container'>
                        <label className='flow-title'>{title}</label>
                    </div>
                    <BolmateRadio onSelect={voidFunction} selected={selected} />
                </div>
            </div>
        );
    }

    function renderAutoUploadOldNumber() {
        return (
            <>
                <div className='icon-container'>
                    <Icon icon={fileTextFilled} className='invoice-icon' />
                </div>
                <label className='text-title'>
                    {t('connectionComplete.invoiceNumber.title', 'Welke factuur nummering wil je hiervoor gebruiken?')}
                </label>
                {renderInvoiceNumberEditor(
                    oldInvoiceNumStart,
                    oldInvoiceNumPrefix,
                    onChangeOldInvoiceNumPrefix,
                    onChangeOldInvoiceNumStart,
                    'oldInvoicePrefix',
                    'oldInvoiceStart',
                    true,
                    'old-number-editor'
                )}
                <div className='button-container-double'>
                    {renderBackButton()}
                    <BolmateButton onClick={onClickNext} label={t('connectionComplete.next', 'Volgende')} />
                </div>
            </>
        );
    }

    function renderNotifications() {
        return (
            <div className='inner-container'>
                <div className='icon-container'>
                    <Icon icon={Bell} className='invoice-icon' />
                </div>
                <label className='text-title'>
                    {t('connectionComplete.notifications.title', 'Welke meldingen wil je ontvangen?')}
                </label>
                <div className='text-subtitle-container flex'>
                    <label className='text-subtitle'>
                        {t(
                            'connectionComplete.notifications.subtitle1',
                            'Selecteer bij welke gebeurtenissen je direct een notificatie per e-mail wilt.'
                        )}
                        <br />
                        {t('connectionComplete.notifications.subtitle2', 'Ik wil een notificatie ontvangen bij:')}
                    </label>
                    <div className='info-container'>
                        <AwesomeIcon name='info-circle' className='info-icon' />
                        <label className='info-text left fw'>
                            {t(
                                'connectionComplete.notifications.info1',
                                'De notificaties worden met standaard instellingen aangemaakt.'
                            )}
                            <br />
                            {t(
                                'connectionComplete.notifications.info2',
                                'Je kunt deze altijd nog aanpassen op de Notificaties pagina.'
                            )}
                        </label>
                    </div>
                </div>
                <div className='notifications-container'>
                    {renderNotification(NotificationType.REVIEWS)}
                    {renderNotification(NotificationType.BUY_BOX)}
                    {renderNotification(NotificationType.STOCK)}
                    {renderNotification(NotificationType.PRODUCT_HIJACK, {beta: true})}
                    {renderNotification(NotificationType.ORDER)}
                </div>
                <div className='button-container-double'>
                    {renderBackButton()}
                    <BolmateButton onClick={onClickNext} label={t('connectionComplete.next', 'Volgende')} />
                </div>
            </div>
        );
    }

    function renderNotification(notificationType: NotificationType, config?: {beta: boolean}) {
        const NotificationTypeParams: Record<NotificationType, NotificationTypeParam> = {
            [NotificationType.REVIEWS]: {
                icon: 'star',
                title: t('connectionComplete.notifications.reviews.title', 'Nieuwe reviews'),
                description: t(
                    'connectionComplete.notifications.reviews.description',
                    'Ontvang een e-mail bij nieuwe reviews'
                )
            },
            [NotificationType.PRODUCT_HIJACK]: {
                icon: 'user-shield',
                title: t('connectionComplete.notifications.productHijack.title', 'Productkaping notificatie'),
                description: t(
                    'connectionComplete.notifications.productHijack.description',
                    'Ontvang een e-mail wanneer een nieuwe verkoper op jouw product verschijnt'
                )
            },
            [NotificationType.BUY_BOX]: {
                icon: 'trophy',
                title: t('connectionComplete.notifications.buyBox.title', 'Koopblok verandering'),
                description: t(
                    'connectionComplete.notifications.buyBox.description',
                    'Een e-mail wanneer het koopblok verandert'
                )
            },
            [NotificationType.STOCK]: {
                icon: 'box-open',
                title: t('connectionComplete.notifications.stock.title', 'Lage voorraad'),
                description: t(
                    'connectionComplete.notifications.stock.description',
                    'Krijg een e-mail als jouw voorraad onder de 20 is'
                )
            },
            [NotificationType.ORDER]: {
                icon: 'truck-fast',
                title: t('connectionComplete.notifications.order.title', 'Nieuwe bestellingen'),
                description: t(
                    'connectionComplete.notifications.order.description',
                    'Ontvang een e-mail bij nieuwe orders'
                )
            }
        };
        const {icon, title, description} = NotificationTypeParams[notificationType];
        return (
            <div className='notification-container' onClick={onToggleNotification(notificationType)}>
                <AwesomeIcon name={icon} className='flow-icon' />
                <div className='texts-container'>
                    <label className='flow-title'>
                        {title}{' '}
                        {config?.beta && (
                            <BolmateBadge
                                label={t('connectionComplete.notifications.betaLabel', 'Beta')}
                                className='hijack-beta-label'
                            />
                        )}
                    </label>
                    <label className='text-example'>{description}</label>
                </div>
                <Checkbox onCheck={voidFunction} checked={enableNotifications.has(notificationType)} />
            </div>
        );
    }

    function renderSummary() {
        return (
            <>
                <Loader active={loading} />
                <div className='icon-container'>
                    <Icon icon='icons8:finish-flag' className='invoice-icon' />
                </div>
                <label className='text-title'>{t('connectionComplete.summary.title', 'Klaar voor de start!')}</label>
                {renderHistorySummary()}
                <div className='button-container-double'>
                    {renderBackButton()}
                    <BolmateButton onClick={onClickNext} label={t('connectionComplete.summary.start', 'Start')} />
                </div>
            </>
        );
    }

    function renderHistorySummary() {
        if (!importHistory) return null;
        return (
            <div className='text-subtitle-container flex'>
                <label className='text-subtitle'>
                    {t('connectionComplete.summary.history1', 'We gaan nu jouw orders importeren.')}
                    <br />
                    {t('connectionComplete.summary.history2', 'Dit kan even duren, afhankelijk van het aantal orders.')}
                    <br />
                    {t(
                        'connectionComplete.summary.history3',
                        'In de tussentijd kun je de rest van het platform gewoon gebruiken.'
                    )}
                    <br />
                    <br />
                    {t('connectionComplete.summary.history4', 'De facturen worden vanaf nu automatisch aangemaakt.')}
                    <br />
                    {t(
                        'connectionComplete.summary.history5',
                        'Je kunt Bolmate nu verder naar wens instellen en gaan gebruiken.'
                    )}
                </label>
                <div className='info-container'>
                    <AwesomeIcon name='info-circle' className='info-icon' />
                    <label className='info-text'>
                        {t(
                            'connectionComplete.importHistory.info1',
                            'De facturen zijn pas beschikbaar als het importeren klaar is.'
                        )}
                        <br />
                        {t(
                            'connectionComplete.importHistory.info2',
                            'In de tussentijd kun je wel de rest van het platform gebruiken.'
                        )}
                        <br />
                        {t('connectionComplete.importHistory.info3', 'Indicatie van de tijd die het duurt:')}
                        <br />
                        <br />
                        {t('connectionComplete.importHistory.time1', '100 orders = 1 minuut')} <br />
                        {t('connectionComplete.importHistory.time2', '1000 orders = 1,5 minuten')} <br />
                        {t('connectionComplete.importHistory.time3', '5000 orders = 2 minuten')}
                        <br />
                        ...
                    </label>
                </div>
            </div>
        );
    }

    function renderStep(): ReactElement {
        switch (step) {
            case ConnectionSettingsStep.INTRO:
                return renderIntro();
            case ConnectionSettingsStep.IMPORT_HISTORY:
                return renderImportHistory();
            case ConnectionSettingsStep.INVOICE_NUMBER:
                return renderInvoiceNumber();
            case ConnectionSettingsStep.AUTO_UPLOAD:
                return renderAutoUpload();
            case ConnectionSettingsStep.AUTO_UPLOAD_OLD:
                return renderAutoUploadOld();
            case ConnectionSettingsStep.AUTO_UPLOAD_OLD_NUMBER:
                return renderAutoUploadOldNumber();
            case ConnectionSettingsStep.NOTIFICATIONS:
                return renderNotifications();
            case ConnectionSettingsStep.COMPLETE:
            case ConnectionSettingsStep.COMPLETE_IMPORT:
                return renderSummary();
        }
    }

    return (
        <div className='connection-complete'>
            <Image name='logo' className='logo' />
            <div
                className={classNames(
                    'main-container',
                    [ConnectionSettingsStep.INTRO, ConnectionSettingsStep.COMPLETE].includes(step) && 'align-center'
                )}
            >
                {renderStep()}
            </div>
        </div>
    );
}
